import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { InformationCircleIcon, PhotoIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { type PatientCloudFile } from '../../../../services/patientFiles';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import {
    uploadSparkPhotos,
    positionForFile,
    SPARK_PHOTO_SLOTS,
    type SparkPhotoPosition
} from '../../../../services/sparkSubmission';
import {
    MIN_SPARK_PHOTOS,
    formatUploadDate,
    photosSignature,
    useCloudFiles,
    type RegisterFooter,
    type RegisterBusy
} from './spark.shared';

//  Step 2 - composite slot grid (dashboard-style) + thumbnail picker

export function UploadPhotosStep({
    patientId,
    onContinue,
    registerFooter,
    registerBusy,
    registerPhotosProgress,
    assignments,
    setAssignments,
    prefilledRef,
    uploadedSig,
    caseId,
    minPhotos = MIN_SPARK_PHOTOS
}: {
    patientId: string;
    onContinue: (uploadedSig: string) => void;
    registerFooter: RegisterFooter;
    registerBusy: RegisterBusy;
    registerPhotosProgress: (progress: { placed: number; total: number } | null) => void;
    assignments: Record<string, string>;
    setAssignments: Dispatch<SetStateAction<Record<string, string>>>;
    prefilledRef: MutableRefObject<boolean>;
    uploadedSig: string | null;
    /** Optional Spark case to upload to (e.g. a refinement case). Defaults to the
     *  patient's stored manufacturer case when omitted. */
    caseId?: number;
    /** Minimum photos required to continue. Defaults to MIN_SPARK_PHOTOS; pass 0
     *  to remove the requirement (e.g. refinements). */
    minPhotos?: number;
}) {
    const { showToast } = useToast();
    const { data: files, isLoading } = useCloudFiles(patientId, 'image');

    const [picking, setPicking] = useState<SparkPhotoPosition | null>(null);
    const [busy, setBusy] = useState(false);

    // Auto-prefill slots from each photo's stored position metadata - once per
    // wizard session (the ref lives in the parent), and only while empty so it
    // never clobbers placements preserved across step navigation.
    useEffect(() => {
        if (prefilledRef.current || !files) return;
        prefilledRef.current = true;
        setAssignments((prev) => {
            if (Object.keys(prev).length > 0) return prev;
            const next: Record<string, string> = {};
            const used = new Set<string>();
            for (const f of files) {
                const pos = positionForFile(f);
                if (pos && !next[pos] && !used.has(f.id)) {
                    next[pos] = f.id;
                    used.add(f.id);
                }
            }
            return Object.keys(next).length > 0 ? next : prev;
        });
    }, [files, prefilledRef, setAssignments]);

    const fileById = useMemo(() => new Map((files ?? []).map((f) => [f.id, f])), [files]);
    const assignedIds = useMemo(() => new Set(Object.values(assignments)), [assignments]);
    const available = useMemo(
        () => (files ?? []).filter((f) => !assignedIds.has(f.id)),
        [files, assignedIds]
    );
    const assignedCount = Object.keys(assignments).length;

    const unassign = (position: string) =>
        setAssignments((a) => {
            const n = { ...a };
            delete n[position];
            return n;
        });

    const currentSig = useMemo(() => photosSignature(assignments), [assignments]);
    // Already sent this exact set to Spark - no need to re-upload, just advance.
    const alreadyUploaded = uploadedSig !== null && uploadedSig === currentSig && assignedCount > 0;

    const handleUpload = useCallback(async () => {
        const pairs = Object.entries(assignments).map(([position, patientFileId]) => ({
            patientFileId,
            position: position as SparkPhotoPosition
        }));
        if (pairs.length === 0) return;
        setBusy(true);
        try {
            const res = await uploadSparkPhotos(patientId, pairs, caseId);
            showToast(`Uploaded ${res.uploaded} photo(s) to Spark.`, 'success');
            onContinue(photosSignature(assignments));
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to upload photos.'), 'error');
            setBusy(false);
        }
    }, [assignments, patientId, caseId, onContinue, showToast]);

    const slotCount = SPARK_PHOTO_SLOTS.length;
    // Locked until at least `minPhotos` slots are placed (0 = no minimum).
    const meetsMinimum = assignedCount >= minPhotos;

    const primaryLabel = busy
        ? 'Uploading…'
        : alreadyUploaded || assignedCount === 0
          ? 'Continue'
          : `Upload ${assignedCount} photo${assignedCount === 1 ? '' : 's'} & continue`;

    const onPrimary = useCallback(() => {
        if (alreadyUploaded) {
            onContinue(currentSig);
            return;
        }
        // Nothing placed — only reachable when there is no minimum; skip the
        // upload and advance.
        if (assignedCount === 0) {
            onContinue(currentSig);
            return;
        }
        void handleUpload();
    }, [alreadyUploaded, assignedCount, onContinue, currentSig, handleUpload]);

    useEffect(() => {
        registerFooter({
            label: primaryLabel,
            onClick: onPrimary,
            disabled: busy || !meetsMinimum
        });
        return () => registerFooter(null);
    }, [registerFooter, primaryLabel, onPrimary, busy, meetsMinimum]);

    // Report placement progress to the wizard's fixed photos sub-header.
    useEffect(() => {
        registerPhotosProgress({ placed: assignedCount, total: slotCount });
        return () => registerPhotosProgress(null);
    }, [registerPhotosProgress, assignedCount, slotCount]);

    // Lock wizard navigation while the upload is in flight.
    useEffect(() => {
        registerBusy(busy);
        return () => registerBusy(false);
    }, [registerBusy, busy]);

    if (isLoading) {
        return <div className="text-sm text-gray-400 py-6 text-center">Loading photos…</div>;
    }

    return (
        <div className="w-full max-w-3xl mx-auto flex flex-col gap-4">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                {SPARK_PHOTO_SLOTS.map((slot) => {
                    const fileId = assignments[slot.position];
                    const file = fileId ? fileById.get(fileId) : undefined;
                    return (
                        <SlotCard
                            key={slot.position}
                            label={slot.label}
                            referenceImage={slot.referenceImage}
                            file={file ?? null}
                            onPick={() => setPicking(slot.position)}
                            onRemove={() => unassign(slot.position)}
                        />
                    );
                })}
            </div>

            {(files?.length ?? 0) === 0 && (
                <div className="flex items-center justify-center gap-2 py-2 text-sm text-gray-400">
                    <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
                    No photos found on this patient.
                    {minPhotos > 0 && ` At least ${minPhotos} photos are required to continue.`}
                </div>
            )}

            {picking && (
                <PhotoPickerModal
                    label={SPARK_PHOTO_SLOTS.find((s) => s.position === picking)?.label ?? ''}
                    files={available}
                    onClose={() => setPicking(null)}
                    onSelect={(fileId) => {
                        setAssignments((a) => ({ ...a, [picking]: fileId }));
                        setPicking(null);
                    }}
                />
            )}
        </div>
    );
}

function SlotCard({
    label,
    referenceImage,
    file,
    onPick,
    onRemove
}: {
    label: string;
    referenceImage: string | null;
    file: PatientCloudFile | null;
    onPick: () => void;
    onRemove: () => void;
}) {
    const hasPhoto = Boolean(file);
    const border = hasPhoto
        ? 'border-green-500 bg-green-50'
        : 'border-dashed border-gray-300 hover:border-gray-400 bg-gray-50';
    return (
        <div className="flex flex-col items-center">
            <div
                className={`relative w-full aspect-square rounded-lg border-2 overflow-hidden transition-all ${border} ${hasPhoto ? '' : 'cursor-pointer'}`}
                onClick={() => !hasPhoto && onPick()}
            >
                {hasPhoto ? (
                    <>
                        <Thumb file={file!} className="w-full h-full object-cover" />
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                            className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                            aria-label="Remove"
                        >
                            <XMarkIcon className="w-3.5 h-3.5 text-gray-600" />
                        </button>
                    </>
                ) : (
                    <>
                        {referenceImage ? (
                            <img
                                src={referenceImage}
                                alt={label}
                                className="w-full h-full object-cover opacity-40"
                            />
                        ) : (
                            <div className="w-full h-full bg-gray-100" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-white/80 rounded-full p-2">
                                <PlusIcon className="w-5 h-5 text-gray-500" />
                            </div>
                        </div>
                    </>
                )}
            </div>
            <p className="mt-1.5 text-[11px] text-gray-700 text-center font-medium">{label}</p>
        </div>
    );
}

function PhotoPickerModal({
    label,
    files,
    onClose,
    onSelect
}: {
    label: string;
    files: PatientCloudFile[];
    onClose: () => void;
    onSelect: (fileId: string) => void;
}) {
    useEscapeKey(onClose, true);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[560px] p-6 relative">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Choose a photo · {label}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-800"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
                {files.length === 0 ? (
                    <p className="text-sm text-gray-500 py-6 text-center">
                        No more photos available to assign.
                    </p>
                ) : (
                    <div className="grid grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
                        {files.map((f) => (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => onSelect(f.id)}
                                className="flex flex-col items-center gap-1 rounded-lg border border-gray-200 p-1.5 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                            >
                                <div className="w-full aspect-square rounded overflow-hidden bg-gray-100">
                                    <Thumb file={f} className="w-full h-full object-cover" />
                                </div>
                                <span className="text-[10px] text-gray-600 truncate w-full text-center">
                                    {f.file.originalFilename}
                                </span>
                                <span className="text-[9px] text-gray-400 w-full text-center">
                                    {formatUploadDate(f.file.createdAt)}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Image thumbnail for a cloud file; falls back to a neutral box if no URL. */
function Thumb({ file, className }: { file: PatientCloudFile; className?: string }) {
    // Keyed to the URL, so a re-signed thumbUrl gets a fresh attempt.
    const [erroredSrc, setErroredSrc] = useState<string | null>(null);
    if (!file.thumbUrl || erroredSrc === file.thumbUrl) {
        return (
            <div className={`bg-gray-200 flex items-center justify-center ${className ?? ''}`}>
                <PhotoIcon className="w-5 h-5 text-gray-400" />
            </div>
        );
    }
    return (
        <img
            src={file.thumbUrl}
            alt={file.file.originalFilename}
            className={className}
            onError={() => setErroredSrc(file.thumbUrl ?? null)}
        />
    );
}
